// render-library.ts — the "catalog-library" renderer: the library pages (library.php / my_library.php).
//
// Same JSON pipeline as the rest of the bundle (PHP compiles a flat payload from fetch_sources(); the
// browser holds it in memory and filters client-side, no jQuery) — but the cards are the source
// (digital product) catalog cards, GROUPED by entitlement: a bare catalog grid, a collapsible FateTide
// Plus bundle, and (My Library) a purchased "Owned" section. The entitlement PARTITION + subscribe-CTA
// flags are decided server-side (options/api/library_sources_list_inc.php) — the client only renders.
//
// Filtering is keyword (campaign-style search box) + a product-type CHIP filter, both client-side. The
// cards are cloned element-for-element from table_list_sources_catalog_inc.php / table_list_my_library_inc.php.

import type {
    AdventureCard,
    CatalogLibraryResponse,
    CatalogListConfig,
    LibraryGroup,
    LibraryProductType,
    LibrarySourceCard,
} from './types';
import { resolveUrl, results, setStatus, showLoader, hideLoader } from './catalog-list';
import { buildEmptyState, emptyStateFromAttributes } from '../shared/empty-state';
import { renderSourceCardElement } from '../shared/source-card';
import { renderAdventureCardElement } from '../shared/adventure-card';

// Close the filter panel (slideToggleEl if available, else hide) — same contract as the other renderers.
function closePanel(panelId: string | null): void {
    if (!panelId) return;
    if (typeof window.slideToggleEl === 'function') {
        window.slideToggleEl(panelId);
        return;
    }
    const panel = document.getElementById(panelId);
    if (panel) panel.style.display = 'none';
}

// Build a group's wrapper + the inner grid the cards go into. Returns the outer element to append, the
// grid to fill, and (for a 'details' bundle) a setter to force the open state during filtering.
interface GroupShell { wrapper: HTMLElement; grid: HTMLElement; setOpen: ((open: boolean) => void) | null; initialOpen: boolean; }

function buildGroupShell(group: LibraryGroup): GroupShell {
    const border = group.border || 'info';

    const makeSubscribeBar = (extraClass: string): HTMLElement | null => {
        if (!group.subscribe?.show) return null;
        const a = document.createElement('a');
        a.href = group.subscribe.href;
        a.className = 'btn btn-info text-dark fw-semibold text-nowrap px-4';
        a.textContent = group.subscribe.label || 'Subscribe';
        if (!extraClass) return a;
        const bar = document.createElement('div');
        bar.className = extraClass;
        bar.appendChild(a);
        return bar;
    };

    const makeIntro = (titleCls: string): HTMLElement => {
        const intro = document.createElement('div');
        intro.className = 'ft-fatetide-plus-bundle__intro';
        const h2 = document.createElement('h2');
        h2.className = 'ft-fatetide-plus-bundle__title ' + titleCls + ' mb-0';
        h2.textContent = group.title;
        intro.appendChild(h2);
        if (group.blurb) {
            const p = document.createElement('p');
            p.className = 'ft-fatetide-plus-bundle__blurb text-muted small mb-0';
            p.textContent = group.blurb;
            intro.appendChild(p);
        }
        return intro;
    };

    if (group.style === 'grid') {
        const grid = document.createElement('div');
        grid.className = 'row gx-0 gy-0 justify-content-start ft-library-sources-catalog-grid';
        return { wrapper: grid, grid, setOpen: null, initialOpen: true };
    }

    if (group.style === 'details') {
        const details = document.createElement('details');
        details.className = `ft-fatetide-plus-bundle ft-fatetide-plus-bundle--library card border border-${border} border-opacity-50 shadow overflow-hidden mt-4 mb-2`;
        details.setAttribute('role', 'group');
        details.setAttribute('aria-label', group.title || 'FateTide Plus');
        if (group.open) details.open = true;

        const summary = document.createElement('summary');
        summary.className = 'ft-fatetide-plus-bundle__summary ft-fatetide-plus-bundle__head px-3 px-md-4 py-3 py-md-4 border-bottom border-' + border + ' border-opacity-25 text-center';
        const sInner = document.createElement('div');
        sInner.className = 'd-flex flex-column align-items-center gap-2';
        sInner.appendChild(makeIntro('h5'));
        summary.appendChild(sInner);
        details.appendChild(summary);

        const subBar = makeSubscribeBar('px-3 py-3 border-bottom border-' + border + ' border-opacity-25 text-center bg-body-secondary bg-opacity-10');
        if (subBar) details.appendChild(subBar);

        const bodyWrap = document.createElement('div');
        bodyWrap.className = 'ft-fatetide-plus-bundle__body px-2 px-md-3 py-3 pb-md-4';
        const grid = document.createElement('div');
        grid.className = 'row gx-0 gy-0 justify-content-start';
        bodyWrap.appendChild(grid);
        details.appendChild(bodyWrap);

        return {
            wrapper: details,
            grid,
            setOpen: (open: boolean) => { details.open = open; },
            initialOpen: !!group.open,
        };
    }

    // 'card' bundle (My Library Owned / Plus).
    const wrapper = document.createElement('div');
    wrapper.className = `ft-fatetide-plus-bundle card border border-${border} border-opacity-50 shadow my-4 overflow-hidden`;
    wrapper.setAttribute('role', 'region');

    const head = document.createElement('div');
    head.className = 'ft-fatetide-plus-bundle__head px-3 px-md-4 py-3 py-md-4 border-bottom border-' + border + ' border-opacity-25 text-center';
    const hInner = document.createElement('div');
    hInner.className = 'd-flex flex-column align-items-center gap-2';
    hInner.appendChild(makeIntro('h4'));
    const subBtn = makeSubscribeBar('');
    if (subBtn) hInner.appendChild(subBtn);
    head.appendChild(hInner);
    wrapper.appendChild(head);

    const bodyWrap = document.createElement('div');
    bodyWrap.className = 'ft-fatetide-plus-bundle__body px-2 px-md-3 py-3 pb-md-4';
    const grid = document.createElement('div');
    grid.className = 'row gx-2 gy-3 justify-content-start';
    bodyWrap.appendChild(grid);
    wrapper.appendChild(bodyWrap);

    return { wrapper, grid, setOpen: null, initialOpen: true };
}

interface BuiltCard { el: HTMLElement; text: string; ptIds: string[]; }
interface BuiltGroup { shell: GroupShell; cards: BuiltCard[]; }

export function mountCatalogLibrary(root: HTMLElement, config: CatalogListConfig): void {
    const container = results(root);
    if (!container) {
        console.error('[catalog-library] mount missing [data-ft-catalog-results]', root);
        return;
    }

    const searchInput = config.searchInputId
        ? (document.getElementById(config.searchInputId) as HTMLInputElement | null)
        : null;
    // The product-type chips live in a container on the page (inside the filter panel).
    const chipsContainer = document.querySelector('[data-ft-library-pt-chips]') as HTMLElement | null;

    const inlineData = readInlinePayload(root);
    if (inlineData) {
        hideLoader(root);
        setStatus(root, '');
        build(root, container, config, inlineData, searchInput, chipsContainer);
        return;
    }

    void (async () => {
        const loadingTimer = setTimeout(() => showLoader(root, config), 250);
        try {
            const res = await fetch(resolveUrl(config.endpoint), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: '{}',
            });
            const raw = await res.text();
            let data: CatalogLibraryResponse;
            try {
                data = JSON.parse(raw) as CatalogLibraryResponse;
            } catch {
                throw new Error(`Non-JSON response (HTTP ${res.status}): ${raw.slice(0, 300)}`);
            }
            const errData = data as unknown as { error?: string; detail?: string };
            if (!res.ok || errData.error) {
                throw new Error(errData.detail || errData.error || `HTTP ${res.status}`);
            }
            clearTimeout(loadingTimer);
            hideLoader(root);
            setStatus(root, '');
            build(root, container, config, data, searchInput, chipsContainer);
        } catch (e) {
            clearTimeout(loadingTimer);
            hideLoader(root);
            console.error('[catalog-library] fetch/render failed', e);
            setStatus(root, '');
            container.innerHTML = '';
            const msg = document.createElement('div');
            msg.className = 'col-12 text-center text-muted py-4';
            msg.textContent = 'Could not load the library. Please try again.';
            container.appendChild(msg);
        }
    })();
}

function readInlinePayload(root: HTMLElement): CatalogLibraryResponse | null {
    const payloadId = root.getAttribute('data-ft-payload-id');
    const el = payloadId
        ? document.getElementById(payloadId)
        : root.querySelector<HTMLScriptElement>('script[type="application/json"][data-ft-catalog-payload]');
    if (!el || !el.textContent) return null;
    try {
        const parsed = JSON.parse(el.textContent);
        return parsed && typeof parsed === 'object' ? parsed as CatalogLibraryResponse : null;
    } catch (e) {
        console.error('[catalog-library] inline payload parse failed', e);
        return null;
    }
}

function renderChips(chipsContainer: HTMLElement, productTypes: LibraryProductType[], onChange: () => void): () => string[] {
    chipsContainer.innerHTML = '';
    if (!productTypes.length) {
        const span = document.createElement('span');
        span.className = 'text-muted small';
        span.textContent = 'No catalog product types.';
        chipsContainer.appendChild(span);
        return () => [];
    }
    const buttons: HTMLButtonElement[] = [];
    for (const pt of productTypes) {
        if (!pt.id || !pt.name) continue;
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'btn btn-sm rounded ft-catalog-pt-chip btn-dark border d-inline-flex align-items-center';
        btn.setAttribute('data-product-type-id', pt.id);
        btn.setAttribute('aria-pressed', 'false');
        btn.textContent = pt.name;
        btn.addEventListener('click', () => {
            const pressed = btn.getAttribute('aria-pressed') === 'true';
            btn.setAttribute('aria-pressed', pressed ? 'false' : 'true');
            // Pressed chips use the light style; unpressed the dark/bordered style (matches the legacy chips).
            btn.classList.toggle('btn-light', !pressed);
            btn.classList.toggle('btn-dark', pressed);
            btn.classList.toggle('border', pressed);
            onChange();
        });
        buttons.push(btn);
        chipsContainer.appendChild(btn);
    }
    return () => buttons.filter((b) => b.getAttribute('aria-pressed') === 'true').map((b) => b.getAttribute('data-product-type-id') || '');
}

function build(
    root: HTMLElement,
    container: HTMLElement,
    config: CatalogListConfig,
    data: CatalogLibraryResponse,
    searchInput: HTMLInputElement | null,
    chipsContainer: HTMLElement | null,
): void {
    container.innerHTML = '';
    const privateIcon = data.icons?.private || '';
    const defaultCoverBase = data.default_cover_base || '';

    const builtGroups: BuiltGroup[] = (data.groups || []).map((group) => {
        const shell = buildGroupShell(group);
        container.appendChild(shell.wrapper);
        const cards: BuiltCard[] = (group.cards || []).map((card) => {
            // Purchased Adventures group (2026-07-22): adventure catalog cards. They carry no
            // product_types, so an active product-type chip filter hides the group — acceptable v1.
            if (group.card_kind === 'adventure') {
                const advCard = card as AdventureCard;
                const el = renderAdventureCardElement(advCard, data.icons?.level || '');
                shell.grid.appendChild(el);
                return {
                    el,
                    text: advCard.text || (advCard.name || '').toLowerCase(),
                    ptIds: [],
                };
            }
            const srcCard = card as LibrarySourceCard;
            const el = renderSourceCardElement(srcCard, { privateIcon, defaultCoverBase });
            shell.grid.appendChild(el);
            return {
                el,
                text: srcCard.text || (srcCard.name || '').toLowerCase(),
                ptIds: (srcCard.product_types || []).map((pt) => pt.id).filter(Boolean),
            };
        });
        return { shell, cards };
    });

    // Single empty state for an empty library or filters that hide every card across all groups.
    const emptyEl = document.createElement('div');
    emptyEl.className = 'col-12 text-center text-muted py-5';
    emptyEl.textContent = 'No library titles match your filters.';
    emptyEl.style.display = 'none';
    container.appendChild(emptyEl);
    const emptySpec = !builtGroups.some((group) => group.cards.length > 0) ? emptyStateFromAttributes(root, 'No library titles match your filters.') : null;
    if (emptySpec) {
        emptyEl.className = 'col-12';
        emptyEl.replaceChildren(buildEmptyState(emptySpec));
    }

    // Hydrate <i class="ft-icon"> placeholders (icon_ph) — e.g. the per-source private icon. This
    // renderer doesn't call initTooltips(), so mount icons explicitly (idempotent).
    if (typeof window.ftMountIcons === 'function') {
        try { window.ftMountIcons(container); } catch { /* footer pass covers init */ }
    }

    // Product-type chip filter (client-side). getSelectedPtIds() reads the pressed chips.
    let getSelectedPtIds: () => string[] = () => [];

    const applyFilter = (): void => {
        const keyword = (searchInput?.value || '').trim().toLowerCase();
        const selectedPt = getSelectedPtIds();
        const filtering = keyword !== '' || selectedPt.length > 0;
        let totalVisible = 0;
        for (const group of builtGroups) {
            let visible = 0;
            for (const { el, text, ptIds } of group.cards) {
                const show = (!keyword || text.includes(keyword))
                    && (selectedPt.length === 0 || ptIds.some((id) => selectedPt.includes(id)));
                el.style.display = show ? '' : 'none';
                if (show) visible++;
            }
            group.shell.wrapper.style.display = visible > 0 ? '' : 'none';
            // A collapsible Plus bundle is force-opened while filtering so its matches are visible,
            // and restored to its initial open state when the filters clear.
            if (group.shell.setOpen) group.shell.setOpen(filtering ? visible > 0 : group.shell.initialOpen);
            totalVisible += visible;
        }
        emptyEl.style.display = totalVisible === 0 ? '' : 'none';
    };

    if (chipsContainer) {
        getSelectedPtIds = renderChips(chipsContainer, data.product_types || [], applyFilter);
    }

    // Live keyword search + the house spyglass/clear trailing icons (same wiring as the other renderers).
    if (searchInput) {
        const box = searchInput.closest('.ft-inline-search-root');
        const glyph = box?.querySelector('[data-ft-inline-search-role="glyph"]') ?? null;
        const clear = box?.querySelector('[data-ft-inline-search-role="clear"]') ?? null;
        const syncTrailing = (): void => {
            if (!glyph || !clear) return;
            const hasText = (searchInput.value || '').trim().length > 0;
            glyph.classList.toggle('d-none', hasText);
            clear.classList.toggle('d-none', !hasText);
        };
        searchInput.addEventListener('input', () => { syncTrailing(); applyFilter(); });
        if (clear) {
            clear.addEventListener('click', (e) => {
                e.preventDefault();
                searchInput.value = '';
                searchInput.focus();
                syncTrailing();
                applyFilter();
            });
        }
        syncTrailing();
    }

    // Filter "apply" button: close the panel, then re-filter (chips already filter live on click).
    if (config.filterButtonId) {
        const btn = document.getElementById(config.filterButtonId);
        btn?.addEventListener('click', () => { closePanel(config.filterPanelId); applyFilter(); });
    }

    // Hover-play header videos: initializeVideoCards() (campaign-init.js) is a one-shot DOMContentLoaded
    // scan, so the cards we injected async need a re-scan to wire their autoplay (it skips .initialized,
    // so this only touches the new ones). Same hydrate pattern as the table renderer's rune re-mount.
    const initVideos = (window as unknown as { initializeVideoCards?: () => void }).initializeVideoCards;
    if (typeof initVideos === 'function') initVideos();

    applyFilter();
}
